/**
 * API œuvres — wrapping autour de `apiClient` (axios).
 *
 * Toutes les fonctions retournent des promesses typées sur `types/works.ts`
 * (auto-générés depuis l'OpenAPI). Aucune transformation BE↔FE — le BE est
 * source de vérité, on s'aligne via codegen.
 *
 * Multipart `createWork` : la metadata est sérialisée en JSON dans une
 * partie `metadata`, les N fichiers attachés en parties `files` (limite
 * BE = 10 fichiers).
 */

import { apiClient } from './client'
import type {
  BulkVariantCreateRequest,
  SeriesCreateRequest,
  SeriesReorderRequest,
  SeriesResponse,
  SeriesWorkSummary,
  VisionJobResponse,
  WorkCreateRequest,
  WorkDeliverableResponse,
  WorkDigitalFormatsPreviewResponse,
  WorkListResponse,
  WorkResponse,
  WorkSortKey,
  WorkStatus,
  WorkUpdateRequest,
  WorkVariantInput,
  WorkVariantUpdateRequest,
  WorkVariantWithCatalogResponse,
} from '@/types/works'

export interface WorkListParams {
  q?: string
  tags?: number[]
  series_id?: number
  status?: WorkStatus
  orientation?: string
  date_from?: string
  date_to?: string
  has_pending_drafts?: boolean
  sort?: WorkSortKey
  // Pagination unifiée S5c — `page` 1-based + `page_size` ; le BE réponde
  // avec `WorkListResponse{items, total, page, page_size, has_next}`.
  page?: number
  page_size?: number
}

function toQueryParams(params: WorkListParams): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  if (params.q) out.q = params.q
  if (params.tags && params.tags.length > 0) out.tags = params.tags.join(',')
  if (params.series_id !== undefined) out.series_id = params.series_id
  if (params.status) out.status = params.status
  if (params.orientation) out.orientation = params.orientation
  if (params.date_from) out.date_from = params.date_from
  if (params.date_to) out.date_to = params.date_to
  if (params.has_pending_drafts === true) out.has_pending_drafts = true
  if (params.sort) out.sort = params.sort
  if (params.page !== undefined) out.page = params.page
  if (params.page_size !== undefined) out.page_size = params.page_size
  return out
}

export const worksApi = {
  list: async (params: WorkListParams = {}): Promise<WorkListResponse> => {
    const { data } = await apiClient.get<WorkListResponse>('/works', {
      params: toQueryParams(params),
    })
    return data
  },

  get: async (id: number): Promise<WorkResponse> => {
    const { data } = await apiClient.get<WorkResponse>(`/works/${id}`)
    return data
  },

  getDigitalFormatsPreview: async (id: number): Promise<WorkDigitalFormatsPreviewResponse> => {
    // Refonte digital 2026-05-14 — preview des formats résolus
    // dynamiquement pour le ZIP de livraison.
    const { data } = await apiClient.get<WorkDigitalFormatsPreviewResponse>(
      `/works/${id}/digital-formats-preview`,
    )
    return data
  },

  create: async (payload: WorkCreateRequest): Promise<WorkResponse> => {
    // Création JSON pure (autosave UX) — les fichiers sont uploadés ensuite
    // via `addMedia`. Le backend ne crée pas de vision_job tant qu'aucune
    // image n'est attachée.
    const { data } = await apiClient.post<WorkResponse>('/works', payload)
    return data
  },

  addMedia: async (id: number, files: File[]): Promise<WorkResponse> => {
    const form = new FormData()
    for (const file of files) {
      form.append('files', file, file.name)
    }
    const { data } = await apiClient.post<WorkResponse>(`/works/${id}/media`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    })
    return data
  },

  /**
   * Variante 1-fichier-1-requête consommée comme `uploader` par
   * `useBulkUploadStack().enqueue(...)` — pool de concurrence en
   * arrière-plan vivant à hauteur d'`<AppShell>`. La route
   * `/works/{id}/media` accepte 1..N fichiers, on en envoie 1 ici. 5 min
   * de timeout pour absorber un RAW lourd (>200 Mo) sur un lien client
   * modeste — le default 30s coupait pendant le transfert réseau, pas
   * le traitement BE.
   */
  addMediaOne: async (id: number, file: File): Promise<WorkResponse> => {
    const form = new FormData()
    form.append('files', file, file.name)
    const { data } = await apiClient.post<WorkResponse>(`/works/${id}/media`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300_000,
    })
    return data
  },

  setCanonicalSource: async (id: number, mediaId: number): Promise<WorkResponse> => {
    // Migration 015 — bascule le source canonical (vitrine grille +
    // hero fiche). L'éditorial Vision reste figé sur l'ancien snapshot
    // (cf. `vision_source_media_id`) — l'UI affiche un badge si différent.
    const { data } = await apiClient.post<WorkResponse>(`/works/${id}/media/${mediaId}/canonical`)
    return data
  },

  deleteMedia: async (id: number, mediaId: number): Promise<WorkResponse> => {
    // Hard-delete d'un source (ratio). Le BE garde-fou : refus si dernier
    // source ou si canonical avec d'autres sources. Les dérivés display/
    // thumb cascadent, MinIO est purgé.
    const { data } = await apiClient.delete<WorkResponse>(`/works/${id}/media/${mediaId}`)
    return data
  },

  update: async (id: number, payload: WorkUpdateRequest): Promise<WorkResponse> => {
    const { data } = await apiClient.patch<WorkResponse>(`/works/${id}`, payload)
    return data
  },

  archive: async (id: number): Promise<void> => {
    await apiClient.post(`/works/${id}/archive`)
  },

  unarchive: async (id: number): Promise<void> => {
    await apiClient.post(`/works/${id}/unarchive`)
  },

  publishToPortfolio: async (id: number): Promise<WorkResponse> => {
    // 422 (seuils FR non atteints) : axios laisse l'erreur remonter avec
    // `error.response.data.detail = { message, missing: [...] }`. Le caller
    // affiche les manques via `<PortfolioPublishButton>`.
    const { data } = await apiClient.post<WorkResponse>(`/works/${id}/publish`)
    return data
  },

  unpublishFromPortfolio: async (id: number): Promise<WorkResponse> => {
    const { data } = await apiClient.post<WorkResponse>(`/works/${id}/unpublish`)
    return data
  },

  duplicate: async (id: number): Promise<WorkResponse> => {
    const { data } = await apiClient.post<WorkResponse>(`/works/${id}/duplicate`)
    return data
  },

  remove: async (id: number): Promise<void> => {
    // `remove` plutôt que `delete` : `delete` est un mot réservé dans certains
    // contextes JS et la lecture est plus claire (`worksApi.remove(id)`).
    await apiClient.delete(`/works/${id}`)
  },

  attachTags: async (id: number, tagIds: number[]): Promise<WorkResponse> => {
    // L'endpoint BE accepte `tag_ids` en Form (multipart/x-www-form-urlencoded
    // avec entrées multiples du même nom).
    const form = new URLSearchParams()
    for (const tagId of tagIds) {
      form.append('tag_ids', String(tagId))
    }
    const { data } = await apiClient.post<WorkResponse>(`/works/${id}/tags`, form, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    return data
  },

  detachTag: async (id: number, tagId: number): Promise<void> => {
    await apiClient.delete(`/works/${id}/tags/${tagId}`)
  },

  getVisionJob: async (id: number): Promise<VisionJobResponse | null> => {
    const { data } = await apiClient.get<VisionJobResponse | null>(`/works/${id}/vision-job`)
    return data
  },

  retryVision: async (id: number): Promise<VisionJobResponse> => {
    const { data } = await apiClient.post<VisionJobResponse>(`/works/${id}/vision/retry`)
    return data
  },

  // ── Variantes (S2.5b → S2.5d) ──────────────────────────────────────────────
  addVariant: async (
    id: number,
    payload: WorkVariantInput,
  ): Promise<WorkVariantWithCatalogResponse> => {
    const { data } = await apiClient.post<WorkVariantWithCatalogResponse>(
      `/works/${id}/variants`,
      payload,
    )
    return data
  },
  bulkAddVariants: async (
    id: number,
    payload: BulkVariantCreateRequest,
  ): Promise<WorkVariantWithCatalogResponse[]> => {
    const { data } = await apiClient.post<WorkVariantWithCatalogResponse[]>(
      `/works/${id}/variants/bulk`,
      payload,
    )
    return data
  },
  updateVariant: async (
    id: number,
    variantId: number,
    payload: WorkVariantUpdateRequest,
  ): Promise<WorkVariantWithCatalogResponse> => {
    const { data } = await apiClient.patch<WorkVariantWithCatalogResponse>(
      `/works/${id}/variants/${variantId}`,
      payload,
    )
    return data
  },
  deleteVariant: async (id: number, variantId: number): Promise<void> => {
    await apiClient.delete(`/works/${id}/variants/${variantId}`)
  },

  // ── Deliverables digitaux (S2.5d) ──────────────────────────────────────────
  listDeliverables: async (id: number, variantId: number): Promise<WorkDeliverableResponse[]> => {
    const { data } = await apiClient.get<WorkDeliverableResponse[]>(
      `/works/${id}/variants/${variantId}/deliverables`,
    )
    return data
  },
  regenerateDeliverables: async (
    id: number,
    variantId: number,
  ): Promise<WorkDeliverableResponse[]> => {
    const { data } = await apiClient.post<WorkDeliverableResponse[]>(
      `/works/${id}/variants/${variantId}/deliverables/regenerate`,
    )
    return data
  },
}

export const seriesApi = {
  list: async (): Promise<SeriesResponse[]> => {
    const { data } = await apiClient.get<SeriesResponse[]>('/series')
    return data
  },

  create: async (payload: SeriesCreateRequest): Promise<SeriesResponse> => {
    const { data } = await apiClient.post<SeriesResponse>('/series', payload)
    return data
  },

  listWorks: async (seriesId: number): Promise<SeriesWorkSummary[]> => {
    const { data } = await apiClient.get<SeriesWorkSummary[]>(`/series/${seriesId}/works`)
    return data
  },

  reorder: async (
    seriesId: number,
    payload: SeriesReorderRequest,
  ): Promise<SeriesWorkSummary[]> => {
    const { data } = await apiClient.patch<SeriesWorkSummary[]>(
      `/series/${seriesId}/positions`,
      payload,
    )
    return data
  },
}
